import type { BrandSpecial } from '@iheartjane/data-access';
import { useGoldConfig } from '@iheartjane/data-access/react-query';
import { shouldShowGold as useShouldShowGold } from '@iheartjane/menu';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import difference from 'lodash-es/difference';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { createContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMatch, useParams } from 'react-router';

import { buildBrandSpecialFilter } from '@jane/search/util';
import { useUserPreferredStoreIds } from '@jane/shared-ecomm/data-access';
import {
  useGetStore,
  useJaneUser,
  useTargetedBrandSpecials,
} from '@jane/shared/data-access';
import { useEcommApp } from '@jane/shared/providers';
import { userLocationToParams } from '@jane/shared/util';

import { useUserPreferences } from '../userPreferencesProvider/userPreferencesProvider';

export interface BrandSpecialsContextType {
  addedBrandSpecialIds: number[];
  brandSpecialFilter: string;
  brandSpecials: BrandSpecial[];
  dismissBanner: (id: number) => void;
  dismissedBanners: number[];
  loadingBrandSpecials?: boolean;
  removedBrandSpecialIds: number[];
  showBrandSpecialAlert?: boolean;
}

interface BrandSpecialCartProduct {
  brand_special_id?: number | null;
}

interface BrandSpecialsProviderProps {
  brandSpecialHasChanged?: boolean;
  cartProducts: readonly BrandSpecialCartProduct[];
  children: ReactNode;
  storeId?: string | number | null;
}

export const BrandSpecialsContext =
  createContext<BrandSpecialsContextType | null>(null);
BrandSpecialsContext.displayName = 'BrandSpecials';

export const useBrandSpecialsContext = () =>
  useContextEnsuringProvider(BrandSpecialsContext);

export const BrandSpecialsProvider = ({
  brandSpecialHasChanged,
  cartProducts,
  children,
  storeId,
}: BrandSpecialsProviderProps) => {
  // when storeId is null, we use store_id from params as a backup
  const { store_id } = useParams();
  const { appMode } = useEcommApp();
  const primaryStoreId = useMemo(
    () => storeId ?? store_id,
    [storeId, store_id]
  );
  const { data: store } = useGetStore(primaryStoreId || '');
  const { data: goldConfig, isPending: isLoadingGoldConfig } = useGoldConfig();
  const { data: janeUser } = useJaneUser();
  const isMenu = useMatch('/stores/:store_id/:slug/menu/*');
  const isMarketplace = appMode === 'default';

  const [cartBrandSpecialIds, setCartBrandSpecialIds] = useState<number[]>([]);
  const [addedBrandSpecialIds, setAddedBrandSpecialIds] = useState<number[]>(
    []
  );
  const [removedBrandSpecialIds, setRemovedBrandSpecialIds] = useState<
    number[]
  >([]);
  const [dismissedBanners, setDismissedBanners] = useState<number[]>([]);

  const {
    userPreferences,
    userLocation: { cityState, coordinates, zipcode, countryCode },
  } = useUserPreferences();

  const hasLocation = !!coordinates?.lat || !!coordinates?.long || !!zipcode;
  const userLocation = userLocationToParams(cityState, countryCode);

  const state = store?.state || userLocation?.state;
  // use state in marketplace and store id in embeds
  const shouldShowGold =
    useShouldShowGold({
      appMode,
      goldConfig: goldConfig || null,
      state,
      storeId: primaryStoreId,
    }) && !isLoadingGoldConfig;

  const { data: userPreferredStoreIds } = useUserPreferredStoreIds({
    coordinates,
    userPreferences,
    zipcode,
  });

  const useUsersStores = Boolean(!isMenu && isMarketplace);
  const storeIdsToUse = useMemo(
    () =>
      useUsersStores && hasLocation
        ? userPreferredStoreIds || []
        : [primaryStoreId],
    [hasLocation, primaryStoreId, useUsersStores, userPreferredStoreIds]
  );

  const brandSpecialStoreIds = storeIdsToUse?.filter(Boolean);

  const enableBrandSpecialsFetch =
    shouldShowGold && !!brandSpecialStoreIds.length;
  const {
    data,
    isFetching: isFetchingBrandSpecials,
    refetch: refetchBrandSpecials,
  } = useTargetedBrandSpecials(
    {
      storeIds: brandSpecialStoreIds.filter(
        (id): id is string | number => id !== undefined
      ),
      appMode,
    },
    {
      enabled: enableBrandSpecialsFetch,
    }
  );

  const brandSpecials = useMemo(() => data?.brand_specials ?? [], [data]);

  const brandSpecialIds = useMemo(
    () => brandSpecials?.map(({ id }) => id),
    [brandSpecials]
  );

  /**
   * Filter for finding all applicable brand specials
   */
  const brandSpecialFilter = useMemo(
    () => buildBrandSpecialFilter(brandSpecialIds),
    [brandSpecialIds]
  );

  const dismissBanner = useMemo(
    () => (id: number) =>
      setDismissedBanners((prevValue) => [...prevValue, id]),
    []
  );

  useEffect(() => {
    const newBrandSpecialIds = uniq<number>(
      cartProducts
        .map(({ brand_special_id }) => brand_special_id)
        .filter((id): id is number => Boolean(id))
        .sort((a, b) => a - b)
    );

    if (!isEqual(cartBrandSpecialIds, newBrandSpecialIds)) {
      const addedIds = difference<number>(
        newBrandSpecialIds,
        cartBrandSpecialIds
      );
      setAddedBrandSpecialIds(addedIds);

      const removedIds = difference(cartBrandSpecialIds, newBrandSpecialIds);
      setRemovedBrandSpecialIds(removedIds);

      setCartBrandSpecialIds(newBrandSpecialIds);
    } else if (
      addedBrandSpecialIds?.length > 0 ||
      removedBrandSpecialIds?.length > 0
    ) {
      setAddedBrandSpecialIds([]);
      setRemovedBrandSpecialIds([]);
    }
  }, [cartProducts, cartBrandSpecialIds]);

  useEffect(() => {
    if (enableBrandSpecialsFetch) {
      refetchBrandSpecials();
    }
  }, [janeUser?.user]);

  const contextValue = useMemo(
    () => ({
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      dismissBanner,
      dismissedBanners,
      loadingBrandSpecials: isFetchingBrandSpecials,
      removedBrandSpecialIds,
      showBrandSpecialAlert:
        brandSpecialHasChanged &&
        (addedBrandSpecialIds.length > 0 || removedBrandSpecialIds.length > 0),
    }),
    [
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      dismissBanner,
      dismissedBanners,
      isFetchingBrandSpecials,
      removedBrandSpecialIds,
    ]
  );

  return (
    <BrandSpecialsContext.Provider value={contextValue}>
      {children}
    </BrandSpecialsContext.Provider>
  );
};
